import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useApi } from "../../hooks/api";
import { ContentOrLoading } from "../../components/Loadings/ContentOrLoading";
import Button from "../../components/Buttons/Button";
import { secondary } from "../../theme";
import { useGlobal } from "../../hooks/global";
import styles from "./ContractReservationPdf.module.css";
import { IContract } from "../../interfaces";
import { Flex } from "@mantine/core";
import { formatDateTime } from "../../utils/formatters";
import { QRCodeSVG } from 'qrcode.react';
import { replaceVariables } from "../../utils/replaceVariables";

function formatPhoneNumber(phone: string): string {
    const cleaned = phone.replace(/\D/g, '');

    return cleaned.length === 11
        ? cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
        : cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
}

function formatCNPJ(cnpj: string): string {
    const cleaned = cnpj.replace(/\D/g, '');

    return cleaned.replace(
        /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
        '$1.$2.$3/$4-$5'
    );
}

const ContractReservationPdf = () => {
    const [params] = useSearchParams();

    const { api } = useApi();
    const { theme } = useGlobal();

    const [data, setData] = useState<IContract>();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const printRef: any = useRef();

    const handlePdfContractReservation = useCallback(async (id: string) => {
        setIsLoading(true)

        try {
            const [dataPdf, { data: variables }] = await Promise.all([
                api.get(`/contracts/by-reservation/${id}`),
                api.get(`/variables-wpp/${id}`)
            ]);

            let text = dataPdf.data.text;
            if(!dataPdf.data.id) {
                text = replaceVariables(variables, dataPdf.data.text);
            }

            setData({ ...dataPdf.data, text });
        } catch (err) {
            console.error("ERRO: ", err)
        }

        setIsLoading(false)
    }, []);

    useEffect(() => {
        const id = params.get("reservation_id");

        if (!id) return;

        handlePdfContractReservation(id);

    }, [params.get("reservation_id")]);

    const qrCodeLink = useMemo(() => {
        const id = params.get("reservation_id");

        if (!id) return;

        return `${process.env.REACT_APP_URL}/contract-reservation?reservation_id=${id}`
    }, [params.get("reservation_id")]);

    return (
        <div style={{ backgroundColor: '#fff', display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center', flexDirection: 'column', fontFamily: 'Poppins, sans-serif', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' } as React.CSSProperties}>
            <Button
                className={`no-print ${styles.btn}`}
                onClick={() => window.print()}
                style={{
                    whiteSpace: "nowrap",
                    fontSize: '14px',
                    maxWidth: 150,
                    marginTop: '90px',
                    marginBottom: '10px'
                }}
                background={secondary[theme]}
                borderColor={secondary[theme]}
                color="#FFF"
                children="Imprimir"
            />
            <ContentOrLoading loading={isLoading} theme="light">
                <div className={styles.container} ref={printRef} style={{ width: '800px', marginBottom: 0 }}>

                    <div style={{ justifyContent: 'end', flex: 1, display: 'flex', marginBottom: '30px' }}>
                        <img
                            style={{ maxHeight: 55 }}
                            src={data?.logo}
                            alt="Logo"
                        />
                    </div>

                    <h2 style={{ textAlign: 'center', marginBottom: '20px' }}>
                        {data?.title}
                    </h2>

                    <div
                        dangerouslySetInnerHTML={{ __html: data?.text || "Nenhum texto encontrado" }}
                    />

                    <div style={{ display: 'flex', gap: 40, fontSize: '10px' }} className="mt-4">
                        <div className="col-xs-4 text-left">
                            <strong>{data?.constantsCompany?.COMPANY_FANTASY_NAME}</strong>
                            <br />
                            CNPJ: {formatCNPJ(data?.constantsCompany?.COMPANY_CNPJ || '')}
                        </div>
                        <div className="col-xs-6 text-left">
                            <strong>Endereço</strong><br />
                            {data?.constantsCompany?.COMPANY_STREET},
                            {data?.constantsCompany?.COMPANY_NUMBER},
                            {data?.constantsCompany?.COMPANY_COMPLEMENT}
                            <br />
                            {data?.constantsCompany?.COMPANY_CITY} {data?.constantsCompany?.COMPANY_DISTRICT}
                            <br />Cep {data?.constantsCompany?.COMPANY_CEP}
                        </div>
                        <div className="col-xs-2 text-left">
                            <strong>Contatos</strong><br />
                            {formatPhoneNumber(data?.constantsCompany?.COMPANY_PHONE || '')}
                            <br />
                        </div>
                    </div>

                </div>

                {data?.signature && (
                    <div className={styles.container} ref={printRef} style={{ width: '800px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid rgba(204, 204, 204, 0.774)', paddingBottom: '15px', marginBottom: '15px' }}>
                            <div>
                                <h2 style={{ margin: '0px', fontSize: '20px', fontWeight: 'bold' }}>Relatório de Assinaturas</h2>
                                <p style={{ margin: '0px', fontSize: '12px' }}>Datas e horários em UTC-0300 (America/Sao_Paulo)</p>
                                <p style={{ margin: '0px', fontSize: '12px' }}>Última atualização em {formatDateTime(data?.updated_at)}</p>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <img src={data?.logo} alt="Logo" style={{ maxHeight: '55px' }} />
                            </div>

                            {/*          <div style={{ justifyContent: 'end', flex: 1, display: 'flex', marginBottom: '30px' }}>
                            <img
                                style={{ maxHeight: 45 }}
                                src={data?.logo}
                                alt="Logo"
                            />
                        </div> */}

                        </div>

                        <Flex>
                            <Flex gap={2} direction="column" style={{ paddingBottom: '15px', marginBottom: '15px', borderBottom: '1px solid rgba(204, 204, 204, 0.774)' }}>
                                <p style={{ margin: 0 }}><strong>Status:</strong> Assinado</p>
                                <p style={{ margin: 0 }}><strong>Documento:</strong> {data?.title}</p>
                                <p style={{ margin: 0 }}><strong>ID Contrato:</strong> {data?.id}</p>
                                <p style={{ margin: 0 }}><strong>Data de criação:</strong> {formatDateTime(data?.signed_at)}</p>
                            </Flex>

                            <Flex gap={2} align='center' justify='flex-end' style={{ flex: 1, paddingBottom: '15px', marginBottom: '15px', borderBottom: '1px solid rgba(204, 204, 204, 0.774)' }}>
                                <QRCodeSVG size={70} value={qrCodeLink || 'https://my.webcheckin.app/'} />
                            </Flex>
                        </Flex>

                        <div style={{ marginTop: '15px' }}>
                            <h3 style={{ fontSize: '14px', marginBottom: '15px', fontWeight: "bold" }}>Assinaturas</h3>

                            <div style={{ border: '1px solid rgb(221, 221, 221)', borderRadius: '8px', }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>

                                    <div style={{ padding: '20px' }}>
                                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                            <span style={{ backgroundColor: secondary[theme], color: 'white', padding: '2px 25px', borderRadius: '15px', fontSize: '11px', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' } as React.CSSProperties}>Titular</span>
                                            <span style={{ backgroundColor: '#05cdff', color: 'white', padding: '2px 25px', borderRadius: '15px', fontSize: '11px', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' } as React.CSSProperties}>Assinado</span>
                                        </div>

                                        <p style={{ marginTop: '10px', marginBottom: '5px', fontWeight: 'bold' }}>{data?.guest_name?.toUpperCase()}</p>
                                        <p style={{ fontSize: '12px', margin: '0px' }}>Data e hora da assinatura: {formatDateTime(data?.created_at)}</p>
                                        <p style={{ fontSize: '12px', margin: '0px' }}>ID User: {data?.guest_id}</p>
                                    </div>

                                    <Flex direction="column" justify="space-between" style={{ borderLeft: '1px solid rgb(221, 221, 221)', padding: '10px 5px' }}>
                                        <p style={{ fontSize: '11px', margin: '0px 0px' }}>Assinatura</p>
                                        <img
                                            style={{ maxWidth: 270 }}
                                            src={data?.signature}
                                            alt="Assinatura"
                                        />
                                        <p style={{ fontSize: '11px', margin: '0px' }}>{data?.guest_name?.toUpperCase()}</p>
                                    </Flex>
                                </div>

                                <div style={{ paddingTop: '20px', borderTop: '1px solid rgba(204, 204, 204, 0.774)' }}>
                                    <Flex style={{ justifyContent: 'space-between', fontSize: '12px', paddingLeft: '20px', paddingRight: '20px', paddingBottom: '20px' }}>

                                        <div style={{ width: '30%' }}>
                                            <p style={{ fontWeight: 'bold', margin: 0 }}>Ponto de autenticação</p>
                                            <p style={{ margin: 0 }}>Foto do rosto (selfie)</p>
                                            <p style={{ margin: 0 }}>Foto do documento</p>
                                        </div>

                                        <div style={{ width: '70%' }}>
                                            <p style={{ margin: 0 }}>Localização aproximada: {data?.location}</p>
                                            <p style={{ margin: 0 }}>Dispositivo: {data?.device}</p>
                                            <p style={{ margin: 0 }}>IP: {data?.device_ip}</p>
                                        </div>

                                    </Flex>
                                </div>
                            </div>
                        </div>

                        <div style={{ marginTop: '20px' }}>
                            <h3 style={{ fontSize: '14px', marginBottom: '15px', fontWeight: "bold" }}>Anexos</h3>

                            <div style={{ border: '1px solid rgb(221, 221, 221)', borderRadius: '8px', padding: '20px' }}>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                    <span style={{ background: 'rgb(224, 224, 224)', padding: '3px 20px', borderRadius: '15px', fontSize: '11px', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' } as React.CSSProperties}>Foto do rosto (selfie)</span>
                                    <span style={{ background: 'rgb(224, 224, 224)', padding: '3px 20px', borderRadius: '15px', fontSize: '11px', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' } as React.CSSProperties}>Foto do documento</span>
                                </div>


                                <div style={{ marginTop: '20px' }}>
                                    <p style={{ margin: '0px', fontWeight: 'bold' }}>{data?.guest_name?.toUpperCase()}</p>
                                    <p style={{ fontSize: '12px', margin: '0px' }}>Data e hora da assinatura: {formatDateTime(data?.created_at)}</p>
                                    <p style={{ fontSize: '12px', margin: '0px' }}>ID User: {data?.guest_id}</p>
                                </div>

                                <div style={{ textAlign: 'center', margin: '20px -20px 0px -20px', padding: '5px', borderTop: '1px solid rgba(204, 204, 204, 0.774)', borderBottom: '1px solid rgba(204, 204, 204, 0.774)' }}>
                                    Data e hora da validação: {formatDateTime(data?.created_at)}
                                </div>

                                <div style={{ margin: '0 -20px -20px -20px' }}>
                                    <Flex>
                                        <div style={{ flex: 1, borderRight: '1px solid rgba(204, 204, 204, 0.774)', position: 'relative', textAlign: 'center', padding: '10px' }}>
                                            <p style={{ fontSize: '11px', margin: '0 0 10px 0' }}>Foto do rosto (selfie)</p>
                                            <img src={data?.selfie_image} alt="Selfie" style={{ width: '100%' }} />
                                            <div style={{ fontSize: '14px', fontWeight: '500', width: '100%', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: '#00000097' }}>
                                                <p style={{ whiteSpace: 'nowrap' }}>----------------------------------</p>
                                                <p style={{ margin: 0 }}>CONFIDENCIAL</p>
                                                <p style={{ margin: 0 }}>{formatDateTime(data?.created_at)}</p>
                                                <p style={{ whiteSpace: 'nowrap' }}>----------------------------------</p>
                                            </div>
                                        </div>

                                        <div style={{ flex: 1, position: 'relative', textAlign: 'center', padding: '10px' }}>
                                            <p style={{ fontSize: '11px', margin: '0 0 10px 0' }}>Foto do documento</p>
                                            <img src={data?.document_image_front} alt="Documento" style={{ width: '100%' }} />
                                            <div style={{ fontSize: '14px', fontWeight: '500', width: '100%', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', color: '#00000097' }}>
                                                <p style={{ whiteSpace: 'nowrap' }}>----------------------------------</p>
                                                <p style={{ margin: 0 }}>CONFIDENCIAL</p>
                                                <p style={{ margin: 0 }}>{formatDateTime(data?.created_at)}</p>
                                                <p style={{ whiteSpace: 'nowrap' }}>----------------------------------</p>
                                            </div>
                                        </div>
                                    </Flex>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </ContentOrLoading>
        </div>
    );
};

export default ContractReservationPdf;
